// One model (02-DESIGN/05-inference/01-models-and-instances.md § the console:
// a model's page — the settings as a form, behind *Change* in the tab's place
// (02-DESIGN/03-console/01-the-console.md § the moves, page by page; § conventions:
// a form), the
// page itself showing them as facts, the defaults counted with *Show more*; *Repoint* changes provider or model
// and keeps the name; *New key* stores a new version — each a form in the
// tab's place, and in the list's place from a Models row; *Remove* ends the
// workload and deletes its settings and its key, confirmed twice with the
// consequence in the person's words; the output and events as any
// workload's). The settings are saved under the version guard, and a conflict
// is said in the person's words.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { pageOffered } from "../../roles/offers.ts";
import {
  Button,
  Field,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  Select,
  TextArea,
  TextInput,
  useLoad,
} from "../../ui/components.tsx";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { NameLink } from "../../ui/ident.tsx";
import { ShowMore } from "../../ui/more.tsx";
import { RowMenu } from "../../ui/table.tsx";
import { Tabs, useTab, type TabSpec } from "../../ui/tabs.tsx";
import { Code, DOCUMENT_SETTINGS } from "../../wire/config.ts";
import {
  CAPABILITIES,
  credentialPath,
  DEFAULT_CALLOUT_DEADLINE,
  egressHost,
  passesAnthropicFilter,
  Provider,
  PROVIDERS,
  Scope,
  settingsText,
  validName,
  type CalloutRequirement,
  type Settings,
} from "../../wire/inference.ts";
import {
  idSubject,
  SERVICE,
  validWorkloadID,
  type StateReply,
  type UpdateRequest,
  type Workload,
} from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import { EventsPane, OutputPane } from "../workloads/panes.tsx";
import { assemble, readFacts, type Model } from "./models.ts";

export function ModelPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  if (!validWorkloadID(id)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "models")}>Models</Link>}
        title="Not a model"
        line={`"${id}" is not a model's id.`}
      />
    );
  }
  return <One id={id} />;
}

/** ModelForm is a form a model's page shows in its Overview tab's place. */
type ModelForm = "change" | "repoint" | "key";

/** ModelTab is one of a model's tabs (01-the-console.md § conventions: the pages that have tabs). */
type ModelTab = "overview" | "output" | "events";

const TABS: readonly TabSpec<ModelTab>[] = [
  { id: "overview", label: "Overview" },
  { id: "output", label: "Output" },
  { id: "events", label: "Events" },
];

function One({ id }: { id: string }) {
  const s = useSurface();
  const navigate = useNavigate();
  const [load, reload] = useLoad(`model/${id}`, async () => {
    const [w, facts, instances] = await Promise.all([
      s.bus.call<Workload>(SERVICE, idSubject("get", id), undefined),
      readFacts(s.bus, id),
      s.bus.models().catch(() => []),
    ]);
    const callouts = facts.callout !== undefined ? await s.bus.callouts().catch(() => undefined) : undefined;
    const [m] = assemble([w], new Map([[id, facts]]), instances, callouts);
    return m as Model;
  });
  const form = useFormState<ModelForm>();
  const [err, setErr] = useState<unknown>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  const [removing, setRemoving] = useState(false);
  const [chosen, setTab] = useTab(TABS);
  // A form is the Overview tab's content: while one is open — after a refresh, or Back
  // and Forward — that tab shows, and stays chosen when the form closes.
  if (form.open !== undefined && chosen !== "overview") {
    setTab("overview");
  }
  const tab = form.open !== undefined ? "overview" : chosen;
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "models")}>Models</Link>
      <span>/</span>
      <span className="mono">{id}</span>
    </>
  );
  if (load.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the model" />
      </>
    );
  }
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={id} />
        <NotAllowed err={load.err} what="This model" />
      </>
    );
  }
  const m = load.value;
  const askedFor = m.name !== "" ? m.name : undefined;

  const remove = async () => {
    setErr(undefined);
    setRemoving(true);
    try {
      await removeModel(s.bus, id);
      void navigate(tenantPath(s.tenant, "models"));
    } catch (e) {
      setRemoving(false);
      setErr(e);
    }
  };

  return (
    <>
      <PageHead
        crumb={crumb}
        title={m.displayName}
        line={
          m.provider !== "" ? (
            <>
              {m.provider} · <span className="mono">{m.model}</span>
              {askedFor !== undefined && (
                <>
                  {" "}
                  · apps ask for <span className="mono">{askedFor}</span>
                </>
              )}
            </>
          ) : (
            "No settings yet: say which provider's model answers it with Change."
          )
        }
        action={
          tab === "overview" && form.open === undefined ? (
            <>
              <Button
                kind={m.settings === undefined ? "primary" : "default"}
                onClick={() => {
                  form.show("change");
                }}
              >
                Change
              </Button>
              <RowMenu
                label={`Actions for ${m.displayName}`}
                entries={[
                  ...(m.settings !== undefined
                    ? [
                        {
                          label: "Repoint",
                          onSelect: () => {
                            form.show("repoint");
                          },
                        },
                      ]
                    : []),
                  {
                    label: "New key",
                    onSelect: () => {
                      form.show("key");
                    },
                  },
                  ...(removing
                    ? []
                    : [
                        {
                          label: "Remove",
                          danger: true as const,
                          confirm: {
                            title: `Remove model ${m.displayName}?`,
                            consequence: removeConsequence(m),
                          },
                          onSelect: () => {
                            void remove();
                          },
                        },
                      ]),
                ]}
              />
            </>
          ) : undefined
        }
      />
      {err !== undefined && <NotAllowed err={err} />}
      {note !== undefined && (
        <Notice kind="ok">
          {note}{" "}
          <button
            type="button"
            className="text-link"
            onClick={() => {
              setNote(undefined);
            }}
          >
            Dismiss
          </button>
        </Notice>
      )}

      {removing && <Pending what="Removing the model" />}
      <Tabs label={m.displayName} tabs={TABS} value={tab} onChange={setTab}>
        {tab === "overview" && form.open === "change" && (
          <SettingsForm
            id={id}
            m={m}
            onClose={form.close}
            onSaved={(what) => {
              form.close();
              setNote(what);
              reload();
            }}
          />
        )}
        {tab === "overview" && form.open === "repoint" && m.settings !== undefined && (
          <Repoint
            id={id}
            m={m}
            heading="h2"
            back="Overview"
            onClose={form.close}
            onSaved={(what) => {
              form.close();
              setNote(what);
              reload();
            }}
          />
        )}
        {tab === "overview" && form.open === "key" && (
          <NewKey
            id={id}
            heading="h2"
            back="Overview"
            onClose={form.close}
            onStored={(what) => {
              form.close();
              setNote(what);
              reload();
            }}
          />
        )}
        {tab === "overview" &&
          (form.open === undefined || (form.open === "repoint" && m.settings === undefined)) && (
            <section className="card">
              <div className="row" style={{ marginBottom: 16 }}>
                <Pill tone={m.status.tone}>{m.status.word}</Pill>
                {m.status.word === "not configured" && (
                  <span className="small muted">
                    {!m.hasSettings && !m.hasKey
                      ? "no settings and no key yet"
                      : !m.hasSettings
                        ? "no settings yet"
                        : "no key yet: store one with New key"}
                  </span>
                )}
                {m.status.beside !== undefined && <span className="small muted">{m.status.beside}</span>}
                {m.status.word === "ambiguous" && (
                  <span className="small muted">
                    another model is asked for by the same name; apps asking for it are refused until one is
                    renamed
                  </span>
                )}
                <span className="grow" />
                {pageOffered(s.role, "workloads") && (
                  <span className="small">
                    <NameLink to={tenantPath(s.tenant, "workloads", id)}>Open as a workload</NameLink>
                  </span>
                )}
              </div>
              <Facts m={m} />
            </section>
          )}
        {tab === "output" && <OutputPane id={id} placement={m.w.placement} />}
        {tab === "events" && <EventsPane id={id} placement={m.w.placement} />}
      </Tabs>
    </>
  );
}

/** Facts are the model's settings as the page shows them, the defaults counted with Show more. */
function Facts({ m }: { m: Model }) {
  const [more, setMore] = useState(false);
  const st = m.settings;
  if (st === undefined) {
    return (
      <p className="small faint">No settings yet. Change says which provider&apos;s model answers it.</p>
    );
  }
  const defaults = Object.entries(st.defaults ?? {});
  return (
    <dl className="kv">
      <dt>Provider</dt>
      <dd>{st.provider}</dd>
      <dt>Model</dt>
      <dd className="mono">{st.model}</dd>
      <dt>Name</dt>
      <dd>
        <span className="mono">{st.name}</span>
        <span className="small muted"> · what apps and workloads ask for</span>
      </dd>
      {st["display-name"] !== undefined && st["display-name"] !== "" && (
        <>
          <dt>Display name</dt>
          <dd>{st["display-name"]}</dd>
        </>
      )}
      {st["base-url"] !== undefined && st["base-url"] !== "" && (
        <>
          <dt>Endpoint</dt>
          <dd className="mono">{st["base-url"]}</dd>
        </>
      )}
      <dt>Serves</dt>
      <dd>
        {(st.capabilities ?? []).length === 0 ? (
          <span className="faint">whatever the provider&apos;s model can do</span>
        ) : (
          (st.capabilities ?? []).join(", ")
        )}
      </dd>
      <dt>Defaults</dt>
      <dd>
        {defaults.length === 0 ? (
          <span className="faint">none</span>
        ) : (
          <>
            {defaults.length === 1 ? "1 field" : `${String(defaults.length)} fields`}{" "}
            <ShowMore open={more} onToggle={setMore} />
            {more && (
              <div className="small mono">
                {defaults.map(([k, v]) => (
                  <div key={k}>
                    {k}: {JSON.stringify(v)}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </dd>
      {m.callout !== undefined && <CalloutFacts c={m.callout} />}
      {m.entry !== undefined && (
        <>
          <dt>Version</dt>
          <dd>{m.entry.revision}</dd>
        </>
      )}
    </dl>
  );
}

/**
 * CalloutFacts is the required document's callout among the model's facts, in
 * the person's words (01-models-and-instances.md § the console: *inspected by
 * pii-detector before every request*): who inspects, what it is sent, and how
 * long it has to answer — required, so not something Change sets.
 */
function CalloutFacts({ c }: { c: CalloutRequirement }) {
  return (
    <>
      <dt>Inspected by</dt>
      <dd>
        <span className="mono">{c.name}</span>
        <span className="small muted"> · before every request · required</span>
        <div className="small muted">{calloutWords(c)}</div>
      </dd>
    </>
  );
}

/** calloutWords says what a callout is sent and how long it has to answer. */
export function calloutWords(c: CalloutRequirement): string {
  const scope = c.scope ?? Scope.whole;
  const sent =
    scope === Scope.whole
      ? "sent the whole conversation"
      : scope === Scope.new
        ? "sent only the turn not yet answered"
        : `sent "${scope}", which the model refuses`;
  return `${sent}; answers within ${c.deadline ?? DEFAULT_CALLOUT_DEADLINE}`;
}

/** removeModel ends a model's workload and deletes its settings and its key. */
export async function removeModel(bus: ReturnType<typeof useSurface>["bus"], id: string): Promise<void> {
  await bus.call<StateReply>(SERVICE, idSubject("retire", id), undefined, 30000);
  const docs = await bus.configuration().list(id);
  for (const d of docs) {
    await bus.configuration().delete(id, d.key.slice(d.key.lastIndexOf(".") + 1));
  }
  await bus
    .vault()
    .delete(credentialPath(id), true)
    .catch(() => undefined);
}

/** removeConsequence says what removing a model does, in the person's words. */
export function removeConsequence(m: Model): string {
  const askedFor = m.name !== "" ? m.name : undefined;
  return `For good: ${askedFor !== undefined ? `apps asking for "${askedFor}" will be refused` : "it stops and never starts again"}. Its settings and its key are deleted with it.`;
}

/** saveSettings writes the whole settings document under the version it was read at; a conflict is answered as such. */
async function saveSettings(
  bus: ReturnType<typeof useSurface>["bus"],
  id: string,
  settings: Settings,
  expected: number | undefined,
): Promise<{ revision: number } | "conflict"> {
  try {
    const e = await bus.configuration().set(id, DOCUMENT_SETTINGS, settingsText(settings), expected);
    return { revision: e.revision };
  } catch (e) {
    if (isRefusal(e, Code.conflict)) {
      return "conflict";
    }
    throw e;
  }
}

const PROVIDER_OPTIONS: { value: string; label: string }[] = PROVIDERS.map((p) => ({ value: p, label: p }));

/** SettingsForm is *Change*: the model's settings as a form, in the Overview tab's place. */
function SettingsForm({
  id,
  m,
  onClose,
  onSaved,
}: {
  id: string;
  m: Model;
  onClose: () => void;
  onSaved: (what: string) => void;
}) {
  const s = useSurface();
  const base = m.settings;
  const [provider, setProvider] = useState(base?.provider ?? Provider.openai);
  const [model, setModel] = useState(base?.model ?? "");
  const [name, setName] = useState(base?.name ?? "");
  const [display, setDisplay] = useState(base?.["display-name"] ?? "");
  const [endpoint, setEndpoint] = useState(base?.["base-url"] ?? "");
  const [caps, setCaps] = useState<string[]>(base?.capabilities ?? []);
  const [defaults, setDefaults] = useState(
    base?.defaults === undefined ? "" : JSON.stringify(base.defaults, null, 2),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [conflict, setConflict] = useState(false);

  let defaultsErr: string | undefined;
  let parsedDefaults: Record<string, unknown> | undefined;
  if (defaults.trim() !== "") {
    try {
      const v: unknown = JSON.parse(defaults);
      if (typeof v !== "object" || v === null || Array.isArray(v)) {
        defaultsErr = "an object of parameters";
      } else {
        parsedDefaults = v as Record<string, unknown>;
      }
    } catch {
      defaultsErr = "not JSON";
    }
  }
  const nameOK = validName(name.trim());
  const host = egressHost(provider, endpoint.trim() === "" ? undefined : endpoint.trim());
  const ready = model.trim() !== "" && nameOK && defaultsErr === undefined && host !== undefined;
  const changed =
    base === undefined ||
    provider !== base.provider ||
    model.trim() !== base.model ||
    name.trim() !== base.name ||
    display.trim() !== (base["display-name"] ?? "") ||
    endpoint.trim() !== (base["base-url"] ?? "") ||
    caps.join(",") !== (base.capabilities ?? []).join(",") ||
    JSON.stringify(parsedDefaults ?? {}) !== JSON.stringify(base.defaults ?? {});
  const egressChanged = base !== undefined && (endpoint.trim() || "") !== (base["base-url"] ?? "");

  const submit = () => {
    if (busy || !ready) {
      return;
    }
    const settings: Settings = { provider, model: model.trim(), name: name.trim() };
    if (display.trim() !== "") {
      settings["display-name"] = display.trim();
    }
    if (endpoint.trim() !== "") {
      settings["base-url"] = endpoint.trim();
    }
    if (parsedDefaults !== undefined) {
      settings.defaults = parsedDefaults;
    }
    if (caps.length > 0) {
      settings.capabilities = caps;
    }
    setErr(undefined);
    setConflict(false);
    setBusy(true);
    // A new name is an update of the registration, which declares the names
    // the instance is vouched for (decision 0026); the settings follow it.
    const rename =
      base !== undefined && name.trim() !== base.name
        ? s.bus.call<StateReply>(
            SERVICE,
            idSubject("update", id),
            { names: [name.trim()] } satisfies UpdateRequest,
            60000,
          )
        : Promise.resolve(undefined);
    rename
      .then(() => saveSettings(s.bus, id, settings, m.entry?.revision))
      .then(
        (r) => {
          setBusy(false);
          if (r === "conflict") {
            setConflict(true);
            return;
          }
          onSaved(`Saved the settings as version ${r.revision}. The model takes them without a restart.`);
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };

  return (
    <FormPage
      title={`Change ${m.displayName}`}
      line="What answers the name, and how: saved under the version guard, taken without a restart."
      back="Overview"
      heading="h2"
      onClose={onClose}
      foot={
        <>
          {m.entry !== undefined && <span className="small faint">version {m.entry.revision}</span>}
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || !ready || !changed}>
            {busy ? "Saving…" : base === undefined ? "Save the settings" : "Save"}
          </Button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {err !== undefined && <NotAllowed err={err} what="Settings" />}
        {conflict && (
          <Notice kind="bad" title="Someone saved a newer version">
            Reload to see it; what you changed here is not saved.
          </Notice>
        )}
        <div className="two">
          <Field label="Provider">
            <Select value={provider} onChange={setProvider} options={PROVIDER_OPTIONS} />
          </Field>
          <Field label="The provider's model" hint="Sent to the provider as written.">
            <TextInput value={model} onChange={setModel} mono required />
          </Field>
        </div>
        <div className="two">
          <Field
            label="Name"
            hint={
              !nameOK && name.trim() !== ""
                ? "Letters, digits, dot, dash and underscore, up to 64."
                : name.trim() !== "" && !passesAnthropicFilter(name.trim())
                  ? "Apps built against the Anthropic API keep only names beginning with claude or anthropic."
                  : "What apps and workloads ask for."
            }
          >
            <TextInput value={name} onChange={setName} mono required />
          </Field>
          <Field label="Display name" hint="The name when empty.">
            <TextInput value={display} onChange={setDisplay} />
          </Field>
        </div>
        <Field
          label="Endpoint"
          hint={
            host === undefined
              ? provider === Provider.jev
                ? "Jev needs its endpoint."
                : "Not a URL."
              : egressChanged
                ? `The model may reach ${host.host} only once its workload's reach is changed too, under Workloads.`
                : "Only where the provider is self-hosted or regional."
          }
        >
          <TextInput value={endpoint} onChange={setEndpoint} type="url" mono placeholder="https://" />
        </Field>
        <div className="field">
          <span className="label">Serves</span>
          <div className="row">
            {CAPABILITIES.map((c) => (
              <label className="checkbox" key={c}>
                <input
                  type="checkbox"
                  checked={caps.includes(c)}
                  onChange={(e) => {
                    setCaps(e.target.checked ? [...caps, c] : caps.filter((x) => x !== c));
                  }}
                />
                {c}
              </label>
            ))}
          </div>
          <span className="hint">None ticked: whatever the provider&apos;s model can do.</span>
        </div>
        <Field
          label="Defaults"
          hint={
            defaultsErr !== undefined
              ? defaultsErr
              : "Parameters applied when a request names none: temperature, max_tokens, effort. JSON, or empty."
          }
        >
          <TextArea
            value={defaults}
            onChange={setDefaults}
            rows={4}
            mono
            placeholder='{ "temperature": 0.2 }'
          />
        </Field>
      </form>
    </FormPage>
  );
}

/** Repoint changes which provider's model answers the name, and keeps the name: no app, prompt or workload changes. */
export function Repoint({
  id,
  m,
  heading,
  back,
  onClose,
  onSaved,
}: {
  id: string;
  m: Model;
  /** h1 in the list's place, h2 in the Overview tab's. */
  heading?: "h1" | "h2";
  /** What the form stands in front of, drawn as its crumb. */
  back: string;
  onClose: () => void;
  onSaved: (what: string) => void;
}) {
  const s = useSurface();
  const base = m.settings as Settings;
  const [provider, setProvider] = useState(base.provider);
  const [model, setModel] = useState(base.model);
  const [endpoint, setEndpoint] = useState(base["base-url"] ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [conflict, setConflict] = useState(false);
  const host = egressHost(provider, endpoint.trim() === "" ? undefined : endpoint.trim());
  const ready = model.trim() !== "" && host !== undefined;
  const reachChanges = (endpoint.trim() || "") !== (base["base-url"] ?? "") || provider !== base.provider;

  const submit = async () => {
    if (busy || !ready) {
      return;
    }
    const settings: Settings = { ...base, provider, model: model.trim() };
    if (endpoint.trim() !== "") {
      settings["base-url"] = endpoint.trim();
    } else {
      delete settings["base-url"];
    }
    setErr(undefined);
    setConflict(false);
    setBusy(true);
    try {
      const r = await saveSettings(s.bus, id, settings, m.entry?.revision);
      if (r === "conflict") {
        setBusy(false);
        setConflict(true);
        return;
      }
      if (reachChanges) {
        // The instance cannot reach a host its workload's reach does not admit: the registration changes with it.
        const body: UpdateRequest = { egress: [`allow@${host.host}:tcp:${host.port}`] };
        await s.bus.call<StateReply>(SERVICE, idSubject("update", id), body, 60000);
      }
      setBusy(false);
      onSaved(
        `${base.name} now answers with ${provider} · ${model.trim()}. Nothing changes for the apps asking for it.`,
      );
    } catch (e) {
      setBusy(false);
      setErr(e);
    }
  };

  return (
    <FormPage
      title={`Repoint ${base.name}`}
      heading={heading}
      back={back}
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            kind="primary"
            onClick={() => {
              void submit();
            }}
            disabled={busy || !ready}
          >
            {busy ? "Repointing…" : "Repoint"}
          </Button>
        </>
      }
    >
      <p className="muted">
        Another provider&apos;s model answers <span className="mono">{base.name}</span> from now on. No app,
        prompt or workload changes; a request in flight finishes on the model it started on.
      </p>
      {err !== undefined && <NotAllowed err={err} what="Repoint" />}
      {conflict && (
        <Notice kind="bad" title="Someone saved a newer version">
          Reload the page to see it, then repoint again.
        </Notice>
      )}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Provider">
          <Select value={provider} onChange={setProvider} options={PROVIDER_OPTIONS} />
        </Field>
        <Field label="The provider's model">
          <TextInput value={model} onChange={setModel} mono required autoFocus />
        </Field>
        <Field
          label="Endpoint"
          hint={
            host === undefined
              ? "Not a URL."
              : reachChanges
                ? `The model's reach changes to ${host.host} with it.`
                : "Only where the provider is self-hosted or regional."
          }
        >
          <TextInput value={endpoint} onChange={setEndpoint} type="url" mono placeholder="https://" />
        </Field>
      </form>
    </FormPage>
  );
}

/** NewKey stores a new version of the provider's key at the model's own path; the model takes it at its next read. */
export function NewKey({
  id,
  heading,
  back,
  onClose,
  onStored,
}: {
  id: string;
  /** h1 in the list's place, h2 in the Overview tab's. */
  heading?: "h1" | "h2";
  /** What the form stands in front of, drawn as its crumb. */
  back: string;
  onClose: () => void;
  onStored: (what: string) => void;
}) {
  const s = useSurface();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const submit = () => {
    if (busy || key === "") {
      return;
    }
    setErr(undefined);
    setBusy(true);
    s.bus
      .vault()
      .store(credentialPath(id), key)
      .then(
        (r) => {
          setBusy(false);
          setKey("");
          onStored(
            `Stored the key as version ${r.version}. The model takes it at its next connection to the provider.`,
          );
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <FormPage
      title="New key"
      heading={heading}
      back={back}
      onClose={onClose}
      foot={
        <>
          <Button kind="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button kind="primary" onClick={submit} disabled={busy || key === ""}>
            {busy ? "Storing…" : "Store"}
          </Button>
        </>
      }
    >
      <p className="muted">
        The provider&apos;s key, stored as a new version under the model&apos;s own path in Secrets. Typed
        once, readable by the model alone, never shown again.
      </p>
      {err !== undefined && <NotAllowed err={err} what="Store" />}
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="API key">
          <TextInput value={key} onChange={setKey} type="password" required autoFocus />
        </Field>
      </form>
    </FormPage>
  );
}
